import { useRef } from "react";
import PageContainer from "@components/layout/PageContainer";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faCopy, faEnvelope, faEye, faPrint, faXmark } from "@fortawesome/free-solid-svg-icons";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import moment from "moment";
import CancelInvoiceOffCanvas from "@components/layout/Offcanvas/CancelInvoiceOffCanvas";
import { OpenCancelInvoicePubSub } from "@utils/pubsubs/PubSub";
import { renderEDocumentStatusOffCanvasTrigger } from "@utils/ModelIndexUtils";
import { ATV_PENDING_CANCEL_MESSAGE, ensureInvoiceAtvAnswered } from "@utils/edocumentAtvGuard";
import { useAppSelector } from "@/store/hooks";

const InvoiceCancel = () => {
  const modelRef = useRef<ModelIndexHandle>(null);
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const einvoiceActive = Boolean(suscriptionData?.einvoice_status);

  const triggerInvoiceCancel = async (item: InvoiceDataType) => {
    // Con facturacion electronica activa no se anula un documento cuyo destino en
    // Hacienda todavia se desconoce: la anulacion quedaria colgando de una factura
    // que aun puede terminar rechazada.
    const canCancel = await ensureInvoiceAtvAnswered(item, einvoiceActive, ATV_PENDING_CANCEL_MESSAGE);
    if (!canCancel) {
      return;
    }
    OpenCancelInvoicePubSub.publish({
      openOffCanvas: true,
      invoice: item
    })
  }

  const onInvoiceCancel = () => {
    modelRef.current?.refetch()
    OpenCancelInvoicePubSub.publish({
      openOffCanvas: false,
      invoice: {}
    })
  }

  return (
    <PageContainer
      title="Anular Factura"
      backUrl="/user/dashboard"
    >
      <ModelIndex<InvoiceDataType>
        ref={modelRef}
        messages={{
          noResults: "No hay Facturas creadas aun"
        }}
        meiliConfig={{
          index: "invoices",
          sort: ["fecha:desc"],
          filter: "status !='anulada'"
        }}
        mandatoryFilters={[{ anulada: { eq: false } }]}
        fetchData={(...args) => InvoiceApi.fetchAll(...args)}
        filterProps={[
          {
            key: "sequence",
            filterType: "contains"
          },
          {
            key: "invoice_key",
            filterType: "contains"
          },
          {
            key: "numero",
            filterType: "eq",
            type: "number"
          }
        ]}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence,
            optionalHighlighted: (val: any, data: InvoiceDataType) => { return data.anulada || false },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            },
          },
          {
            key: (d) => d.nombre_factura || d.client?.nombre || "",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: (data) => renderEDocumentStatusOffCanvasTrigger(data, { model: "Invoice", id: `${data.id}` }),
            title: "Estado ATV"
          },
          {
            key: (data) => {
              if ("currency_id" in data) {
                return formatMoneyValue(data.total, (data as any).currency_id)
              }
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "3" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            onClick: (id, item) => triggerInvoiceCancel(item),
            actionTitle: "Anular Factura",
            iconProps: { icon: faXmark, className: styles.cancelBtn }
          },
        ]}
      >
      </ModelIndex>
      <CancelInvoiceOffCanvas onInvoiceCancel={onInvoiceCancel} />
    </PageContainer >
  );
}

export default InvoiceCancel;
